import { createClient } from '@supabase/supabase-js'

// These will be set via environment variables in production
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'your-supabase-url'
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'your-supabase-anon-key'

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Auth helpers
export const auth = {
  // Sign up new user
  async signUp(email, password) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    
    if (error) throw error
    return data
  },

  // Sign in existing user
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) throw error
    return data
  },

  // Sign out current user
  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Get current user
  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return user
  },

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Database helpers
// Updated database helpers in src/lib/supabase.js
export const db = {
  // Songs
  async getSongs(genre = null, difficulty = null) {
    let query = supabase.from('songs').select('*')
    
    if (genre && genre !== 'daily') {
      query = query.eq('genre', genre)
    }
    
    if (difficulty) {
      query = query.eq('difficulty', difficulty)
    }
    
    const { data, error } = await query
    if (error) throw error
    return data
  },

  async getSongById(id) {
    const { data, error } = await supabase
      .from('songs')
      .select('*')
      .eq('id', id)
      .single()
    
    if (error) throw error
    return data
  },

  // User profiles
  async createUserProfile(userId, email, favoriteGenre = 'daily') {
    const { data, error } = await supabase
      .from('users')
      .insert([
        {
          id: userId,
          email,
          favorite_genre: favoriteGenre
        }
      ])
      .select()
      .single()
    
    if (error) {
      console.error('Database error creating profile:', error)
      throw error
    }
    return data
  },

  async getUserProfile(userId) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (error) throw error
    return data
  },

async updateUserProfile(userId, updates) {
  const { data, error } = await supabase
    .from('users')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single()
  
  if (error) throw error
  return data
},

async checkUsernameAvailable(username, excludeUserId = null) {
  let query = supabase
    .from('users')
    .select('id')
    .eq('username', username)
  
  if (excludeUserId) {
    query = query.neq('id', excludeUserId)
  }
  
  const { data, error } = await query
  
  if (error) throw error
  return data.length === 0
},

// Add this function inside the db object, after getUserProfile or anywhere in the db object
async getGameResult(userId, songId, gameDate, genre, difficulty) {
  const { data, error } = await supabase
    .from('game_results')
    .select('*')
    .eq('user_id', userId)
    .eq('song_id', songId)
    .eq('game_date', gameDate)
    .eq('genre', genre)
    .eq('difficulty', difficulty)
    .maybeSingle()  // Changed from .single() to .maybeSingle()
  
  if (error) {
    console.error('getGameResult error:', error)
    throw error
  }
  
  return data  // Will be null if no rows found, which is fine
},

// Also add the saveGameResult function if it's missing:
async saveGameResult(gameResult) {
  const { data, error } = await supabase
    .from('game_results')
    .insert([gameResult])
    .select()
    .single()
  
  if (error) {
    console.error('Database error saving game result:', error)
    throw error
  }
  
  console.log('Database: Game result saved:', data)
  return data
},

// Leaderboard functions (for future use)
async getLeaderboard(limit = 10, genre = null) {
  let query = supabase
    .from('game_results')
    .select(`
      user_id,
      users!inner(username, display_name),
      score,
      won,
      attempts,
      game_date
    `)
    .eq('completed', true)
    .eq('won', true)
    .order('score', { ascending: false })
    .limit(limit)
  
  if (genre && genre !== 'daily') {
    query = query.eq('genre', genre)
  }
  
  const { data, error } = await query
  if (error) throw error
  
  // Group by user and get their best scores
  const userBestScores = {}
  data.forEach(result => {
    const userId = result.user_id
    if (!userBestScores[userId] || result.score > userBestScores[userId].score) {
      userBestScores[userId] = result
    }
  })
  
  return Object.values(userBestScores)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
},

async getUserRank(userId, genre = null) {
  // Get user's best score
  let userQuery = supabase
    .from('game_results')
    .select('score')
    .eq('user_id', userId)
    .eq('completed', true)
    .eq('won', true)
    .order('score', { ascending: false })
    .limit(1)
  
  if (genre && genre !== 'daily') {
    userQuery = userQuery.eq('genre', genre)
  }
  
  const { data: userData, error: userError } = await userQuery
  if (userError) throw userError
  if (!userData.length) return null
  
  const userBestScore = userData[0].score
  
  // Count how many users have better scores
  let rankQuery = supabase
    .from('game_results')
    .select('user_id, score')
    .eq('completed', true)
    .eq('won', true)
    .gt('score', userBestScore)
  
  if (genre && genre !== 'daily') {
    rankQuery = rankQuery.eq('genre', genre)
  }
  
  const { data: rankData, error: rankError } = await rankQuery
  if (rankError) throw rankError
  
  // Count unique users with better scores
  const betterUsers = new Set(rankData.map(r => r.user_id))
  
  return {
    rank: betterUsers.size + 1,
    score: userBestScore
  }
},

  // Fixed: Get user game results with manual join instead of foreign key relationship
  async getUserGameResults(userId, limit = 100) {
    try {
      // First get the game results
      const { data: gameResults, error: gameError } = await supabase
        .from('game_results')
        .select('*')
        .eq('user_id', userId)
        .order('game_date', { ascending: false })
        .limit(limit)
      
      if (gameError) throw gameError
      
      if (!gameResults || gameResults.length === 0) {
        return []
      }
      
      // Get unique song IDs from the game results
      const songIds = [...new Set(gameResults.map(result => result.song_id))]
      
      // Fetch the songs data separately
      const { data: songs, error: songError } = await supabase
        .from('songs')
        .select('id, title, artist, genre')
        .in('id', songIds)
      
      if (songError) {
        console.warn('Failed to fetch songs data:', songError)
        // Return game results without song data if songs fetch fails
        return gameResults.map(result => ({
          ...result,
          songs: null
        }))
      }
      
      // Create a map of songs by ID for quick lookup
      const songsMap = songs.reduce((map, song) => {
        map[song.id] = song
        return map
      }, {})
      
      // Combine game results with song data
      return gameResults.map(result => ({
        ...result,
        songs: songsMap[result.song_id] || null
      }))
      
    } catch (error) {
      console.error('Error in getUserGameResults:', error)
      throw error
    }
  },

  // Enhanced getUserStats with better error handling
async getUserStats(userId) {
  try {
    const { data, error } = await supabase
      .from('game_results')
      .select('won, completed, game_date, created_at')
      .eq('user_id', userId)
      .eq('completed', true)
      .order('game_date', { ascending: false })
      .order('created_at', { ascending: false }) // Add this second ordering
    
    if (error) throw error
    
    if (!data || data.length === 0) {
      return {
        gamesPlayed: 0,
        gamesWon: 0,
        gamesCompleted: 0,
        winPercentage: 0,
        currentStreak: 0,
        maxStreak: 0
      }
    }
    
    const totalGames = data.length
    const gamesWon = data.filter(game => game.won).length
    const winPercentage = totalGames > 0 ? Math.round((gamesWon / totalGames) * 100) : 0
    
    // Calculate current streak (counting from most recent game)
    let currentStreak = 0
    for (const game of data) {
      if (game.won) {
        currentStreak++
      } else {
        break // Stop counting when we hit a loss
      }
    }
    
    // Calculate max streak
    let maxStreak = 0
    let tempStreak = 0
    for (const game of data) {
      if (game.won) {
        tempStreak++
        maxStreak = Math.max(maxStreak, tempStreak)
      } else {
        tempStreak = 0
      }
    }
    
    console.log('User stats calculated:', {
      totalGames,
      gamesWon,
      currentStreak,
      maxStreak,
      recentGames: data.slice(0, 5).map(g => ({ 
        won: g.won, 
        date: g.game_date, 
        time: g.created_at 
      }))
    })
    
    return {
      gamesPlayed: totalGames,
      gamesWon,
      gamesCompleted: totalGames,
      winPercentage,
      currentStreak,
      maxStreak
    }
    
  } catch (error) {
    console.error('Error in getUserStats:', error)
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      gamesCompleted: 0,
      winPercentage: 0,
      currentStreak: 0,
      maxStreak: 0
    }
  }
}
}

// Utility function to check if user is authenticated
export const isAuthenticated = () => {
  return supabase.auth.getUser().then(({ data: { user } }) => !!user)
}

// Utility function to require authentication
export const requireAuth = async () => {
  const user = await auth.getUser()
  if (!user) {
    throw new Error('Authentication required')
  }
  return user
}